import { useState } from 'react';
import { Calculator } from 'lucide-react';
import { SEOHead } from '@/components/atoms/SEOHead';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { AdBanner } from '@/components/ads/AdBanner';
import { cn } from '@/lib/cn';

/* ── 계산 상수 ──────────────────────────────────────────────── */
const FEE_RATE = 0.00015; // 매수 수수료 0.015%
const TAX_RATE = 0.0018; // 증권거래세 0.18% (코스피·코스닥 통합 기준)
const UPPER_LIMIT = 1.3; // 상한가 +30%

/** 추가 투자 시나리오 — 빠른 선택 금액 프리셋 (원) */
const EXTRA_INVEST_PRESETS: Array<{ label: string; value: number }> = [
  { label: '50만원', value: 500_000 },
  { label: '100만원', value: 1_000_000 },
  { label: '300만원', value: 3_000_000 },
  { label: '500만원', value: 5_000_000 },
  { label: '1,000만원', value: 10_000_000 },
];

/* ── 숫자 유틸 ──────────────────────────────────────────────── */
/** 입력값(콤마 포함 문자열)에서 숫자만 추출 */
function parseNum(v: string): number {
  const n = Number(v.replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}
/** 금액 표기 — decimals 자리까지 표시(불필요한 끝자리 0은 생략) */
function won(n: number, decimals = 0): string {
  return `${n.toLocaleString('ko-KR', { maximumFractionDigits: decimals })}원`;
}
/** 입력 중 천단위 콤마 자동 표시용(빈 문자열은 그대로) */
function withCommas(raw: string): string {
  const digits = raw.replace(/[^0-9]/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('ko-KR');
}

interface CalcResult {
  addQty: number; // 필요 수량
  addCost: number; // 필요 투자금
  fee: number; // 수수료
  feePerShare: number; // 1주당 수수료
  totalQty: number; // 총 보유 수량
  totalCost: number; // 총 투자금(기존+추가)
  tax: number; // 매도 증권거래세
  taxPerShare: number; // 1주당 증권거래세
  upToAvg: number; // 현재주가→현재평단 상한가 횟수
  upToTarget: number; // 현재주가→목표평단 상한가 횟수
  currentPrice: number; // 현재 주가 — 1~10연상 가격표 계산 기준
  avgPrice: number; // 현재 평균 단가 — 추가 투자 시나리오 계산용
  holdQty: number; // 보유 수량 — 추가 투자 시나리오 계산용
  targetAvg: number; // 목표 평균 단가 — 추가 투자 시나리오 계산용
}

export function AveragingDownCalculatorPage() {
  const [avg, setAvg] = useState(''); // 현재 평균 단가
  const [qty, setQty] = useState(''); // 보유 수량
  const [price, setPrice] = useState(''); // 현재 주가
  const [target, setTarget] = useState(''); // 목표 평균 단가
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CalcResult | null>(null);

  const handleCalc = () => {
    const a = parseNum(avg); // 현재평단
    const q = parseNum(qty); // 보유수량
    const p = parseNum(price); // 현재주가
    const t = parseNum(target); // 목표평단

    // 입력 검증
    if (a <= 0 || q <= 0 || p <= 0 || t <= 0) {
      setResult(null);
      setError('모든 항목에 0보다 큰 값을 입력해주세요.');
      return;
    }
    if (p >= a) {
      setResult(null);
      setError('현재 주가가 현재 평단보다 낮아야 물타기 계산이 가능해요.');
      return;
    }
    if (t >= a) {
      setResult(null);
      setError('목표 평단은 현재 평단보다 낮게 입력해주세요.');
      return;
    }
    if (t <= p) {
      setResult(null);
      setError('목표 평단은 현재 주가보다 높아야 해요. (아무리 매수해도 평단은 현재 주가까지만 내려가요)');
      return;
    }

    // 추가수량 = 보유수량 × (현재평단 - 목표평단) / (목표평단 - 현재주가) → 올림
    const addQty = Math.ceil((a * q - t * q) / (t - p));
    const addCost = p * addQty; // 필요 투자금
    const fee = p * addQty * FEE_RATE; // 매수 수수료
    const totalQty = q + addQty; // 총 보유 수량
    const totalCost = a * q + addCost; // 총 투자금(기존+추가)
    const tax = t * totalQty * TAX_RATE; // 매도 증권거래세

    const upToAvg = Math.ceil(Math.log(a / p) / Math.log(UPPER_LIMIT));
    const upToTarget = Math.ceil(Math.log(t / p) / Math.log(UPPER_LIMIT));

    setError(null);
    setResult({
      addQty,
      addCost,
      fee,
      feePerShare: addQty > 0 ? fee / addQty : 0,
      totalQty,
      totalCost,
      tax,
      taxPerShare: totalQty > 0 ? tax / totalQty : 0,
      upToAvg,
      upToTarget,
      currentPrice: p,
      avgPrice: a,
      holdQty: q,
      targetAvg: t,
    });
  };

  const handleEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleCalc();
  };

  return (
    <div className="container-base py-8 md:py-10">
      <SEOHead
        title="물타기 계산기 - 시세공간"
        description="목표 평균 단가에 도달하기 위해 추가로 매수해야 하는 수량과 투자금, 수수료, 증권거래세, 상한가 횟수까지 한 번에 계산해보세요."
        canonical="/calculator/averaging-down"
      />

      <div className="mx-auto max-w-5xl">
        {/* 헤더 */}
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--color-primary)] text-[var(--color-primary-inv)]">
            <Calculator className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-2xl font-extrabold leading-tight">물타기 계산기</h1>
            <p className="text-sm text-[var(--color-text-sub)]">
              목표 평단까지 필요한 추가 매수 수량과 비용을 계산해요.
            </p>
          </div>
        </div>

        {/* PC: 좌(입력) / 우(결과) 2분할 · 모바일: 세로 적층 */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
          {/* 좌측 — 입력 폼 */}
          <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-sm md:p-6">
            <h2 className="mb-4 text-base font-bold text-[var(--color-text)]">정보 입력</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="현재 평균 단가" unit="원" value={avg}
                onChange={(v) => setAvg(withCommas(v))} onKeyDown={handleEnter} />
              <Field label="보유 수량" unit="주" value={qty}
                onChange={(v) => setQty(withCommas(v))} onKeyDown={handleEnter} />
              <Field label="현재 주가" unit="원" value={price}
                onChange={(v) => setPrice(withCommas(v))} onKeyDown={handleEnter} />
              <Field label="목표 평균 단가" unit="원" value={target}
                onChange={(v) => setTarget(withCommas(v))} onKeyDown={handleEnter} />
            </div>

            {error && (
              <p className="mt-4 rounded-xl bg-[var(--color-rise)]/10 px-3 py-2.5 text-sm text-[var(--color-rise)]">
                {error}
              </p>
            )}

            <Button fullWidth size="lg" className="mt-5 rounded-xl" onClick={handleCalc}>
              계산하기
            </Button>
          </div>

          {/* 우측 — 결과 (계산 전 안내 플레이스홀더) */}
          <div>
            {result ? (
              <div className="space-y-5">
                {/* 1) 투자비용 */}
                <ResultCard step={1} title="투자비용">
                  <div className="grid grid-cols-2 gap-3">
                    <HeroStat label="필요 수량" value={Math.round(result.addQty).toLocaleString('ko-KR')} unit="주" />
                    <HeroStat
                      label="필요 투자금"
                      value={result.addCost.toLocaleString('ko-KR', { maximumFractionDigits: 0 })}
                      unit="원"
                      variant="soft"
                    />
                  </div>
                  <div className="mt-4 space-y-2.5">
                    <Row label="수수료" value={won(result.fee, 2)} sub={`1주당 ${won(result.feePerShare, 2)}`} />
                  </div>
                  <Note>수수료는 0.015%를 기준으로 산정하였으며, 증권사 및 유형에 따라 차이가 있습니다.</Note>
                </ResultCard>

                {/* 2) 매수 후 예상 결과 */}
                <ResultCard step={2} title="매수 후 예상 결과">
                  <div className="grid grid-cols-2 gap-3">
                    <HeroStat label="총 보유 수량" value={Math.round(result.totalQty).toLocaleString('ko-KR')} unit="주" />
                    <HeroStat
                      label="총 투자금"
                      value={result.totalCost.toLocaleString('ko-KR', { maximumFractionDigits: 0 })}
                      unit="원"
                      variant="soft"
                    />
                  </div>
                  <p className="mt-2 text-center text-xs text-[var(--color-text-sub)]">
                    총 투자금 = 기존 투자금 + 추가 투자금
                  </p>
                  <div className="mt-4 space-y-2.5">
                    <Row
                      label="목표 평단 달성 후 매도 증권거래세"
                      value={won(result.tax, 2)}
                      sub={`1주당 ${won(result.taxPerShare, 2)}`}
                    />
                  </div>
                  <Note>
                    코스피(증권거래세: 0.03% + 농어촌특별세: 0.15%), 코스닥(증권거래세: 0.18%) 모두 0.18%로 산정하였습니다.
                  </Note>
                </ResultCard>

                {/* 3) 추가 투자 시나리오 (DEV) — 빠른선택/직접입력 금액 기준 매수 시뮬레이션 */}
                <ResultCard step={3} title="추가 투자 시나리오">
                  <ExtraInvestScenario
                    key={`${result.currentPrice}-${result.avgPrice}-${result.holdQty}-${result.targetAvg}`}
                    currentPrice={result.currentPrice}
                    avgPrice={result.avgPrice}
                    holdQty={result.holdQty}
                    targetAvg={result.targetAvg}
                  />
                </ResultCard>

                {/* 4) 연속 상한가 시나리오 */}
                <ResultCard step={4} title="연속 상한가 시나리오">
                  <div className="grid grid-cols-2 gap-3">
                    <MiniStat label="현재 나의 평균단가까지" value={result.upToAvg} />
                    <MiniStat label="목표 평균단가까지" value={result.upToTarget} />
                  </div>

                  {/* 1~10연상 가격 표 — 현재주가 × 1.3ⁿ (원 단위 버림) */}
                  <UpperLimitTable
                    currentPrice={result.currentPrice}
                    upToAvg={result.upToAvg}
                    upToTarget={result.upToTarget}
                  />

                  <Note>
                    상한가(+30%)가 연속으로 도달할 경우 계산입니다.
                    <br />
                    수수료 및 증권사 및 유형에 따라 차이가 있습니다.
                  </Note>
                </ResultCard>
              </div>
            ) : (
              <div className="flex h-full min-h-[260px] flex-col items-center justify-center rounded-3xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-8 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--color-bg)] text-[var(--color-text-sub)]">
                  <Calculator className="h-6 w-6" />
                </span>
                <p className="mt-3 text-sm font-medium text-[var(--color-text)]">계산 결과가 여기에 표시돼요</p>
                <p className="mt-1 text-xs text-[var(--color-text-sub)]">
                  왼쪽에 값을 입력하고 계산하기를 눌러주세요.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* 페이지 하단 광고 — multiplex-h 전역 비활성화로 display-rect 로 교체 (2026-05-29) */}
        <AdBanner slot="display-rect" className="mt-8 md:mt-10" />
      </div>
    </div>
  );
}

/* ── 입력 필드 ──────────────────────────────────────────────── */
function Field({
  label,
  unit,
  value,
  onChange,
  onKeyDown,
}: {
  label: string;
  unit: string;
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-[var(--color-text)]">{label}</span>
      <div className="relative">
        <Input
          type="text"
          inputMode="numeric"
          placeholder="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          className="h-11 rounded-xl pr-9 text-right text-base font-semibold tabular-nums"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[var(--color-text-sub)]">
          {unit}
        </span>
      </div>
    </label>
  );
}

/* ── 결과 카드 ──────────────────────────────────────────────── */
function ResultCard({
  step,
  title,
  icon,
  children,
}: {
  step: number;
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-sm md:p-6">
      <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-[var(--color-text)]">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-primary)] text-xs font-bold text-[var(--color-primary-inv)]">
          {step}
        </span>
        {title}
        {icon && <span className="text-[var(--color-text-sub)]">{icon}</span>}
      </h2>
      {children}
    </section>
  );
}

/** 핵심 값 강조 박스 — filled(primary 배경) / soft(연한 강조). 2열 그리드에서 나란히 사용 */
function HeroStat({
  label,
  value,
  unit,
  variant = 'filled',
}: {
  label: string;
  value: string;
  unit: string;
  variant?: 'filled' | 'soft';
}) {
  const filled = variant === 'filled';
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-2xl px-3 py-5 text-center',
        filled
          ? 'bg-[var(--color-primary)] text-[var(--color-primary-inv)]'
          : 'border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/[0.06]',
      )}
    >
      <span className={cn('text-xs', filled ? 'opacity-80' : 'text-[var(--color-text-sub)]')}>
        {label}
      </span>
      <span className="mt-1.5 leading-none">
        <span
          className={cn(
            'text-2xl font-extrabold tabular-nums md:text-[1.75rem]',
            !filled && 'text-[var(--color-primary)]',
          )}
        >
          {value}
        </span>
        <span
          className={cn(
            'ml-0.5 text-base font-bold',
            filled ? 'opacity-90' : 'text-[var(--color-text-sub)]',
          )}
        >
          {unit}
        </span>
      </span>
    </div>
  );
}

/** 작은 강조 박스 — 2열 시나리오용 */
function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-5 text-center">
      <span className="text-xs leading-snug text-[var(--color-text-sub)]">{label}</span>
      <span className="mt-2 leading-none">
        <span className="text-3xl font-extrabold tabular-nums text-[var(--color-primary)]">{value}</span>
        <span className="ml-1 text-sm font-bold text-[var(--color-text-sub)]">번</span>
      </span>
    </div>
  );
}

function Row({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-dashed border-[var(--color-border)] pb-2.5 last:border-0 last:pb-0">
      <span className="text-sm text-[var(--color-text-sub)]">{label}</span>
      <span className="text-right">
        <span className="text-base font-bold tabular-nums text-[var(--color-text)]">{value}</span>
        {sub && <span className="mt-0.5 block text-xs text-[var(--color-text-sub)]">{sub}</span>}
      </span>
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-4 rounded-xl bg-[var(--color-bg)] px-3 py-2.5 text-xs leading-relaxed text-[var(--color-text-sub)]">
      {children}
    </p>
  );
}

/* ── 추가 투자 시나리오 (DEV) ─────────────────────────────────────
   빠른 선택/직접 입력한 추가 투자금액 기준으로 매수 가능 수량·실투자금·잔여금·
   매수 후 평단·목표 달성 여부를 시뮬레이션.
   - 매수가능수량 = floor(입력금액 / 현재주가)
   - 매수후평단 = (현재평단×보유수량 + 현재주가×매수수량) / (보유수량+매수수량) */
interface ExtraCalc {
  buyQty: number;
  realInvest: number;
  remain: number;
  newAvg: number; // 매수 후 평단 (원 단위 반올림)
  gapToTarget: number; // 목표평단 - 매수후평단 (음수면 미달)
  achieved: boolean;
}

function ExtraInvestScenario({
  currentPrice,
  avgPrice,
  holdQty,
  targetAvg,
}: {
  currentPrice: number;
  avgPrice: number;
  holdQty: number;
  targetAvg: number;
}) {
  const [amountStr, setAmountStr] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  const [calc, setCalc] = useState<ExtraCalc | null>(null);

  const compute = (amount: number) => {
    if (!Number.isFinite(amount) || amount <= 0) {
      setCalc(null);
      return;
    }
    const buyQty = Math.floor(amount / currentPrice);
    const realInvest = buyQty * currentPrice;
    const remain = amount - realInvest;
    const newAvgExact =
      buyQty > 0
        ? (avgPrice * holdQty + currentPrice * buyQty) / (holdQty + buyQty)
        : avgPrice;
    const newAvg = Math.round(newAvgExact);
    setCalc({
      buyQty,
      realInvest,
      remain,
      newAvg,
      gapToTarget: targetAvg - newAvg,
      achieved: newAvg <= targetAvg,
    });
  };

  const handlePreset = (value: number) => {
    setSelectedPreset(value);
    setAmountStr(value.toLocaleString('ko-KR'));
    compute(value); // 즉시 계산
  };

  const handleAmountChange = (raw: string) => {
    setSelectedPreset(null); // 직접 입력 수정 시 버튼 선택 해제
    setAmountStr(withCommas(raw));
  };

  const handleCalc = () => compute(parseNum(amountStr));
  const handleEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleCalc();
  };

  return (
    <div className="space-y-4">
      {/* 1) 빠른 선택 버튼 */}
      <div>
        <span className="mb-2 block text-sm font-medium text-[var(--color-text)]">빠른 선택</span>
        <div className="flex flex-wrap gap-2">
          {EXTRA_INVEST_PRESETS.map((pr) => (
            <button
              key={pr.value}
              type="button"
              onClick={() => handlePreset(pr.value)}
              className={cn(
                'rounded-full border px-3.5 py-1.5 text-sm font-semibold transition',
                selectedPreset === pr.value
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-[var(--color-primary-inv)]'
                  : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:border-[var(--color-primary)]',
              )}
            >
              {pr.label}
            </button>
          ))}
        </div>
      </div>

      {/* 2) 직접 입력 + 계산 */}
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Field
            label="추가 투자금액"
            unit="원"
            value={amountStr}
            onChange={handleAmountChange}
            onKeyDown={handleEnter}
          />
        </div>
        <Button size="lg" className="h-11 shrink-0 rounded-xl px-5" onClick={handleCalc}>
          계산하기
        </Button>
      </div>

      {/* 3) 결과 */}
      {calc && (
        <div className="space-y-3">
          <div className="space-y-2.5">
            <Row label="매수 가능 수량" value={`${calc.buyQty.toLocaleString('ko-KR')}주`} />
            <Row label="실제 투자금" value={won(calc.realInvest)} />
            <Row label="잔여금" value={won(calc.remain)} />
            <Row label="매수 후 평균단가" value={won(calc.newAvg)} />
            <Row label="목표 평단까지 차이" value={won(calc.gapToTarget)} />
          </div>
          <div
            className={cn(
              'rounded-xl px-3 py-3 text-center text-sm font-bold',
              calc.achieved ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700',
            )}
          >
            {calc.achieved
              ? '✅ 목표 평단 달성 가능해요!'
              : `❌ 목표 평단에 ${(calc.newAvg - targetAvg).toLocaleString('ko-KR')}원 부족해요.`}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── 1~10연상 가격 표 ────────────────────────────────────────────
   현재주가 기준 연속 상한가(×1.3) 시 예상 주가를 1~10연상까지 표로 표시.
   - 가격 = floor(현재주가 × 1.3ⁿ), 천단위 콤마
   - 현재 평단 도달 연상(upToAvg) 행: 파란색 / 목표 평단 도달 연상(upToTarget) 행: 초록색
     (두 값이 같은 행이면 목표 평단(초록) 우선 표시) */
function UpperLimitTable({
  currentPrice,
  upToAvg,
  upToTarget,
}: {
  currentPrice: number;
  upToAvg: number;
  upToTarget: number;
}) {
  const rows = Array.from({ length: 10 }, (_, i) => {
    const n = i + 1;
    return { n, price: Math.floor(currentPrice * Math.pow(UPPER_LIMIT, n)) };
  });

  return (
    <div className="mt-4">
      <h3 className="mb-2 text-sm font-bold text-[var(--color-text)]">현재 주가 기준 1~10연상 가격</h3>
      <div className="overflow-hidden rounded-xl border border-[var(--color-border)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--color-bg)] text-[var(--color-text-sub)]">
              <th className="px-3 py-2 text-left text-xs font-medium">연상</th>
              <th className="px-3 py-2 text-right text-xs font-medium">예상 주가</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ n, price }) => {
              const isTarget = n === upToTarget;
              const isAvg = n === upToAvg;
              return (
                <tr
                  key={n}
                  className={cn(
                    'border-t border-[var(--color-border)]',
                    isTarget
                      ? 'bg-green-50 font-bold text-green-700'
                      : isAvg
                      ? 'bg-blue-50 font-bold text-blue-700'
                      : 'text-[var(--color-text)]',
                  )}
                >
                  <td className="px-3 py-2 text-left tabular-nums">{n}연상</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {price.toLocaleString('ko-KR')}원
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* 범례 */}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-text-sub)]">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-blue-400" />
          현재 평단 도달{upToAvg > 10 ? ' (10연상 초과)' : ` (${upToAvg}연상)`}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-green-400" />
          목표 평단 도달{upToTarget > 10 ? ' (10연상 초과)' : ` (${upToTarget}연상)`}
        </span>
      </div>
    </div>
  );
}
